<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="saveBilling"
      size="md-lg"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="
            w-100
            d-flex
            flex-row
            justify-content-between
            align-items-center
          "
        >
          <h5 class="modal-title">Encounter details</h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="$emit('onModify')"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div class="d-flex justify-content-between align-items-center w-100">
          <div class="text-left mt-1 mr-2">
            <b-button
              v-if="billingId && isPracticeSuiteActive"
              class="ml-2"
              variant="success"
              @click="submitForBilling"
              >Submit for billing</b-button
            >
          </div>
          <div class="text-right mt-1 mr-2">
            <b-button @click="$emit('onModify')" variant="secondary"
              >Cancel</b-button
            >
            <b-button class="ml-2" variant="primary" @click="saveBilling"
              >Save</b-button
            >
          </div>
        </div>
      </template>

      <div class="position-relative">
        <div class="d-flex">
          <div class="client-form-container position-relative w-100 bg-white">
            <div class="section-body">
              <b-row>
                <b-col cols="4" class="modal-col">
                  <b-form-group label="Patient">
                    <b-row>
                      <template v-slot:label=""> Patient </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{ billing.mrnEocn ? billing.mrnEocn + " " : ""
                      }}{{ billing.patient }}
                    </h6>
                  </b-form-group>
                  <b-form-group label="Alerts">
                    <b-row>
                      <template v-slot:label=""> Alerts </template>
                    </b-row>
                    <div class="d-flex flex-row flex-wrap">
                      <div
                        v-for="(alert, index) in billing.patientAlerts"
                        :key="index"
                        class="mr-2"
                      >
                        <i
                          class="alert-note-icon fas fa-exclamation-circle text-danger"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="${alert}"
                        >
                          {{ alert }}
                        </i>
                      </div>
                    </div>
                  </b-form-group>
                  <b-form-group label="Insurance">
                    <b-row>
                      <template v-slot:label=""> Insurance </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{
                        Object.values(
                          billing.scheduleEventParticipantInsurances
                        ).every(value => value === null)
                          ? ""
                          : Object.keys(
                              billing.scheduleEventParticipantInsurances
                            )
                              .map(
                                key =>
                                  billing.scheduleEventParticipantInsurances[
                                    key
                                  ]?.company.name
                              )
                              .filter(x => x != null)
                              .join(", ")
                      }}
                    </h6>
                  </b-form-group>
                  <b-form-group label="Appointment status">
                    <b-row>
                      <template v-slot:label=""> Appointment status </template>
                      <span
                        class="badge-v2 py-2 badge-fixed-size"
                        :style="[
                          billing.appointmentStatus
                            ? {
                                color: billing.appointmentStatus.color,
                                backgroundColor: lightenDarkenColor(
                                  billing.appointmentStatus.color,
                                  85
                                )
                              }
                            : ''
                        ]"
                        >{{
                          billing.appointmentStatus
                            ? billing.appointmentStatus.name
                            : ""
                        }}</span
                      >
                    </b-row>
                  </b-form-group>
                  <b-form-group label="Date & time">
                    <b-row>
                      <template v-slot:label=""> Date & time </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{ billing.date ? formatDate(billing.date) : "" }}
                    </h6>
                  </b-form-group>
                  <b-form-group label="Provider">
                    <b-row>
                      <template v-slot:label=""> Provider </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{ billing.provider }}
                    </h6>
                  </b-form-group>
                  <b-form-group
                    v-if="billing.scheduleEventIsDayInLoC"
                    label="Billable group session hours completed"
                  >
                    <b-row>
                      <template v-slot:label="">
                        Billable group session hours completed
                      </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{ billing.billableGroupSessionHoursCompleted }}
                      <span>
                        out of
                        {{ groupSessionsHoursRequiredForBilling }} hours.</span
                      >
                    </h6>
                  </b-form-group>
                  <b-form-group
                    v-if="
                      billing.facilitators && billing.facilitators.length > 0
                    "
                    label="Facilitators"
                  >
                    <b-row>
                      <template v-slot:label=""> Facilitator </template>
                    </b-row>
                    <div class="mt-1">
                      <div
                        v-for="facilitator in billing.facilitators"
                        :key="facilitator.userId"
                      >
                        <span class="ml-1">
                          {{ formatUser(facilitator.user) }}
                        </span>
                      </div>
                    </div>
                  </b-form-group>
                  <b-form-group
                    v-if="billing.placeOfService"
                    label="Place of service"
                  >
                    <b-row>
                      <template v-slot:label=""> Place of service </template>
                    </b-row>
                    <h6 class="mb-0">
                      {{ billing.placeOfService.code }}-{{
                        billing.placeOfService.name
                      }}
                    </h6>
                  </b-form-group>
                  <b-form-group
                    v-if="
                      billing.linkedDocuments &&
                        billing.linkedDocuments.length > 0
                    "
                    label="Linked documents"
                  >
                    <b-row>
                      <template v-slot:label=""> Linked documents </template>
                    </b-row>
                    <div class="mt-1" style="font-size: 14px">
                      <div
                        v-for="document in billing.linkedDocuments"
                        :key="document.formId"
                        class="
                          mb-0
                          text-primary
                          cursor-pointer
                          d-flex
                          align-items-center
                        "
                        style="line-height: 22.4px"
                        @click="openLinkedDocument(document)"
                      >
                        <i
                          v-if="
                            document.document &&
                              document.document.status &&
                              document.document.status.toLowerCase() !=
                                'completed'
                          "
                          style="font-size: 19px"
                          class="fa fa-clock-o"
                          :class="{
                            'text-danger':
                              document.document.status &&
                              document.document.status.toLowerCase() == 'draft',
                            'text-warning':
                              document.document.status &&
                              document.document.status.toLowerCase() ==
                                'pending'
                          }"
                        ></i>
                        <i
                          v-if="
                            document.document &&
                              document.document.status &&
                              document.document.status.toLowerCase() ==
                                'completed'
                          "
                          style="font-size: 19px"
                          class="fa fa-check text-success"
                        ></i>
                        <i
                          v-if="!document.document"
                          style="font-size: 19px"
                          class="fa fa-clock-o text-danger"
                        ></i>
                        <span class="ml-1 text-underline">
                          {{ document.form.name }}
                        </span>
                      </div>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col class="modal-col">
                  <b-form-group label="Diagnosis codes">
                    <multiple-select
                      :options="icdCodes"
                      v-model="icdCodeIds"
                      :search="
                        (search, loading) =>
                          fetchIcdCodeOptions(search, loading)
                      "
                      :disableFilter="true"
                      placeholder="Search for ICD Codes"
                      :onDemand="true"
                      :disableCache="true"
                    />
                  </b-form-group>
                  <b-form-group
                    label="Services"
                    :state="servicesErrors.value < 0 ? true : false"
                    :invalid-feedback="servicesErrors.join(', ')"
                  >
                    <multiple-select-unit-rate
                      :options="srvOptions"
                      v-model="selectedServices"
                    />
                  </b-form-group>
                  <patient-insurances
                    v-if="eocInsurances"
                    :insurances="billing.insuranceCompanies"
                    :eocInsurances="eocInsurances"
                    :eocBillType="billing.patientBillType"
                    @formPatientInsurancesData="addPatientInsurancesData"
                    @costVariations="getServicesCostVariations"
                    @setIsSelfPay="setIsSelfPay"></patient-insurances>
                  <b-form-group label="Authorization Number">
                    <b-form-input v-model="authorizationNumber"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <document-add-edit-modal
      v-if="showAddDocumentEditor"
      v-model="showAddDocumentEditor"
      :formId="editDocument.formId"
      :clientId="editDocument.clientId"
      :eocId="editDocument.eocId"
      :submitId="editDocument.submitId"
      :scheduleEventId="billing.scheduleEventId"
      :scheduleEventParticipantId="billing.scheduleEventParticipantId"
      @onModify="onModifyAddDocument"
    />
    <document-view-modal
      v-if="documentViewModal.show"
      v-model="documentViewModal.show"
      :documentId="documentViewModal.id"
      @onModify="onModifyAddDocument"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { formatDate } from "../../../helpers/date";
import { lightenDarkenColor } from "../../../helpers/general";
import { validateEncountersForSubmit } from "../../../helpers/encounter";
import billingServer from "../../../server/billing";
import icdCodeServer from "../../../server/icdCode";
import serviceServer from "../../../server/service";
import userServer from "../../../server/user";
import diagnosisCodeServer from "../../../server/diagnosisCode";
import DocumentAddEditModal from "../../episode_of_care/document/DocumentAddEditModal.vue";
import DocumentViewModal from "../../episode_of_care/document/DocumentViewModal.vue";
import MultipleSelect from "../../controls/MultipleSelect.vue";
import MultipleSelectUnitRate from "../../controls/MultipleSelectUnitRate.vue";
import { validateNumberNotNegative } from "../../../helpers/validation";
import PatientInsurances from "../../../components/episode_of_care/PatientInsurances.vue";
import scheduleEventServer from "../../../server/scheduleEvent";

export default {
  name: "BillingViewModal",
  components: {
    DocumentAddEditModal,
    MultipleSelect,
    DocumentViewModal,
    MultipleSelectUnitRate,
    PatientInsurances
  },
  props: ["billingId"],
  data() {
    return {
      billing: {},
      services: [],
      servicesOptions: [],
      icdCodes: [],
      selectedServices: [],
      icdCodeIds: [],
      showAddDocumentEditor: false,
      editDocument: null,
      documentViewModal: {
        show: false,
        id: null
      },
      groupSessionsHoursRequiredForBilling: null,
      authorizationNumber: "",
      servicesErrors: [],
      formPatientInsurances: {},
      eocInsurances: null,
      serviceCostVariations: [],
      isSelfPay: false,
      servicesEmpty: false,
      isLoading: true
    };
  },
  async mounted() {
    await this.fetchServices();

    if (this.billingId) {
      this.fetchBillingData();
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    setIsSelfPay(val) {
      this.isSelfPay = val;
      this.isLoading = false;
    },
    validateServices(services) {
      var withoutErrors = true;
      this.servicesErrors = [];
      for (let i = 0; i < services.length; i++) {
        const service = services[i];
        if (
          !validateNumberNotNegative(service.costPerUnit) ||
          !validateNumberNotNegative(service.numberOfUnits)
        ) {
          this.servicesErrors.push("This field must have a positive value");
          withoutErrors = false;
          break;
        }
      }
      if (this.selectedServices.length === 0) {
        this.servicesErrors.push("This field is required");
        withoutErrors = false;
      }
      return withoutErrors;
    },
    async fetchBillingData() {
      this.billing = await billingServer.get(this.billingId);
      const users = await userServer.getUserServices();
      const providers = users.filter(user => user.providerInfo);
      const provider = providers.find(
        user => user.id === this.billing.providerId
      );
      this.servicesOptions = provider
        ? this.services.filter(s => provider.services?.includes(s.value))
        : this.services;

      this.groupSessionsHoursRequiredForBilling = this.servicesOptions.find(
        x => x.value == this.billing.services[0].id
      )?.groupSessionsHoursRequiredForBilling;
      let srv = this.splitServicesByBillingCodes(this.billing);
      this.selectedServices = srv.map(service => ({
        value: service.id,
        label: `${service.code} - ${service.name}`,
        data: service,
        costPerUnit: service.costPerUnit,
        numberOfUnits: service.numberOfUnits,
        billingCodes: service.billingCodes
      }));
      this.getServicesCostVariations();
      this.icdCodeIds = this.billing.diagnosisCodes.map(icdCode => ({
        value: icdCode.id,
        label: `${icdCode.code} - ${icdCode.description}`,
        data: icdCode
      }));
      this.authorizationNumber = this.billing.authorizationNumber;
      this.eocInsurances = {
        id: this.billing.scheduleEventParticipantId,
        insurancePrimary: this.billing.scheduleEventParticipantInsurances
          .insurancePrimary,
        insuranceSecundary: this.billing.scheduleEventParticipantInsurances
          .insuranceSecundary,
        insuranceTertiary: this.billing.scheduleEventParticipantInsurances
          .insuranceTertiary
      };
    },
    formatDate,
    lightenDarkenColor,
    async addPatientInsurancesData(data) {
      this.formPatientInsurances.primaryPatientInsuranceId =
        data.insurancePrimary.value;
      this.formPatientInsurances.secondaryPatientInsuranceId =
        data.insuranceSecundary.value;
      this.formPatientInsurances.tertiaryPatientInsuranceId =
        data.insuranceTertiary.value;
      this.formPatientInsurances.billTypes = data.billTypes;
      if (
        this.formPatientInsurances &&
        this.selectedServices &&
        this.selectedServices.length > 0 &&
        this.billing
      ) {
        await this.getServicesCostVariations();
      }
    },
    async saveBilling() {
      const groupedServices = this.groupServices();
      const data = {
        id: this.billingId,
        services: groupedServices.map(service => ({
          serviceId: service.serviceId,
          costPerUnit:
            service.billingCodes.length && service.billingCodes[0].costPerUnit
              ? parseFloat(service.billingCodes[0].costPerUnit)
              : 0,
          numberOfUnits:
            service.billingCodes.length && service.billingCodes[0].numberOfUnits
              ? parseFloat(service.billingCodes[0].numberOfUnits)
              : 0,
          billingCodes: service.data.billingCodes?.map(code => ({
            ...code,
            scheduleEventId: this.billing.scheduleEventId,
            serviceId: service.serviceId
          }))
        })),
        diagnosisCodes: this.icdCodeIds.map(x => x.value),
        authorizationNumber: this.authorizationNumber,
        scheduleEventParticipant: {
          primaryPatientInsuranceId: this.formPatientInsurances
            .primaryPatientInsuranceId,
          secondaryPatientInsuranceId: this.formPatientInsurances
            .secondaryPatientInsuranceId,
          tertiaryPatientInsuranceId: this.formPatientInsurances
            .tertiaryPatientInsuranceId,
          billTypes: this.formPatientInsurances.billTypes ? 0 : 1
        }
      };
      if (this.validateServices(data.services)) this.updateBilling(data);
    },
    splitServicesByBillingCodes(encounter) {
      let services = encounter.services || [];
      let result = [];

      services.forEach(service => {
        let billingCodes =
          service.billingCodes.length > 0
            ? service.billingCodes
            : [{ ...service, mod1: "", mod2: "", mod3: "", mod4: "" }];

        billingCodes.forEach(billingCode => {
          let byBillingCode = {
            ...service,
            billingCodes: [billingCode]
          };

          result.push(byBillingCode);
        });
      });

      return result;
    },
    groupServices() {
      const result = [];
      const groups = {};

      this.selectedServices.forEach(item => {
        const serviceId = item.value;
        if (!groups[serviceId]) {
          groups[serviceId] = [];
          result.push({ serviceId, services: groups[serviceId] });
        }
        groups[serviceId].push(item);
      });

      return result.map(item => ({
        ...item,
        data: item.services[0],
        billingCodes: [].concat(
          ...item.services.map(service => service.billingCodes)
        )
      }));
    },
    submitForBilling() {
      this.$dialog
        .confirm(
          { title: "Are you sure you want to submit this charge?" },
          { view: "confirm" }
        )
        .then(async () => {
          const groupedServices = this.groupServices();
          const data = {
            id: this.billingId,
            services: groupedServices.map(service => ({
              serviceId: service.serviceId,
              costPerUnit:
                service.billingCodes.length &&
                service.billingCodes[0].costPerUnit
                  ? parseFloat(service.billingCodes[0].costPerUnit)
                  : 0,
              numberOfUnits:
                service.billingCodes.length &&
                service.billingCodes[0].numberOfUnits
                  ? parseFloat(service.billingCodes[0].numberOfUnits)
                  : 0,
              billingCodes: service.billingCodes?.map(code => ({
                ...code,
                scheduleEventId: this.billing.scheduleEventId,
                serviceId: service.serviceId
              }))
            })),
            diagnosisCodes: this.icdCodeIds.map(x => x.value),
            authorizationNumber: this.authorizationNumber,
            scheduleEventParticipant: {
              primaryPatientInsuranceId: this.formPatientInsurances
                .primaryPatientInsuranceId,
              secondaryPatientInsuranceId: this.formPatientInsurances
                .secondaryPatientInsuranceId,
              tertiaryPatientInsuranceId: this.formPatientInsurances
                .tertiaryPatientInsuranceId,
              billTypes: this.formPatientInsurances.billTypes ? 0 : 1
            }
          };
          if (!this.validateServices(data.services)) return;
          await this.updateBilling(data, false);
          this.billing = await billingServer.get(this.billingId);
          const errors = validateEncountersForSubmit([this.billing]);
          if (errors.length) {
            this.showError({
              message: "Errors: \r\n" + errors.join("\r\n"),
              multiline: true
            });
            return;
          }
          let submitResult = await billingServer.submit([this.billingId]);
          if (submitResult.errorCode) {
            this.showError({
              message: "Error: " + submitResult.message,
              multiline: true
            });
          } else {
            this.$emit("onModify");
          }
        })
        .catch(function() {});
    },
    async updateBilling(billing, closeModal = true) {
      await billingServer.update(this.billingId, billing);
      this.showAlertSuccess();
      if (closeModal) {
        this.$emit("onModify");
      }
    },
    async fetchServices() {
      const services = await serviceServer.query({
        type: "Service"
      });
      this.services = services.map(service => ({
        value: service.id,
        label: `${
          service.billingCodes.length > 0
            ? service.billingCodes[0].cptCode + " - "
            : ""
        } ${service.name}`,
        data: service,
        costPerUnit: service.costPerUnit,
        numberOfUnits: 1,
        groupSessionsHoursRequiredForBilling:
          service.groupSessionsHoursRequiredForBilling,
        billingCodes: service.billingCodes.map(bc => ({
          ...bc,
          numberOfUnits: 1,
          mod1: bc.mod1 ? bc.mod1 : "",
          mod2: bc.mod2 ? bc.mod2 : "",
          mod3: bc.mod3 ? bc.mod3 : "",
          mod4: bc.mod4 ? bc.mod4 : ""
        }))
      }));
    },
    async getServicesCostVariations() {
      if (this.isLoading) return;
      const scheduleEvent = await scheduleEventServer.get(this.billing.scheduleEventId);
      const queryVariations = {
        serviceIds: this.selectedServices.map(service => service.value),
        credentialId: this.billing.userCredentialId,
        supervisorCredentialId: this.billing.userSupervisorCredentialId,
        scheduleEventStatusId: this.billing.appointmentStatus.id,
        payorIds: this.isSelfPay
          ? []
          : [
              this.billing.insuranceCompanies.find(
                x =>
                  x.id == this.formPatientInsurances.primaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              this.billing.insuranceCompanies.find(
                x =>
                  x.id == this.formPatientInsurances.secondaryPatientInsuranceId
              )?.filteredInsuranceCompanyId,
              this.billing.insuranceCompanies.find(
                x =>
                  x.id == this.formPatientInsurances.tertiaryPatientInsuranceId
              )?.filteredInsuranceCompanyId
            ],
        patientBillType: this.isSelfPay
          ? 0
          : this.formPatientInsurances.billTypes
          ? 0
          : 1,
        startDateTime: scheduleEvent.startDateTime
      };
      let variations = await serviceServer.getCostVariations(queryVariations);
      if(variations.length > 0){
        variations = variations.filter(
          variation =>
            !this.selectedServices.some(service =>
              this.hasThisVariation(service, variation)
            )
        );
        
        this.selectedServices.forEach(service => {
          variations.forEach(variation => {
            if (service.value === variation.serviceId) {
              service.billingCodes[0].costPerUnit = "";
              service.billingCodes[0].costPerUnit = variation.costPerUnit;
              service.billingCodes[0].mod1 = "";
              service.billingCodes[0].mod1 = variation.mod1;
              service.billingCodes[0].mod2 = "";
              service.billingCodes[0].mod2 = variation.mod2;
              service.billingCodes[0].mod3 = "";
              service.billingCodes[0].mod3 = variation.mod3;
              service.billingCodes[0].mod4 = "";
              service.billingCodes[0].mod4 = variation.mod4;
              service.variationId = variation.id;
            }
          });
        });
      }
    },
    hasThisVariation(service, variation) {
      return (
        service.variationId == variation.id &&
        service.billingCodes[0].mod1 == variation.mod1 &&
        service.billingCodes[0].mod2 == variation.mod2 &&
        service.billingCodes[0].mod3 == variation.mod3 &&
        service.billingCodes[0].mod4 == variation.mod4 &&
        service.billingCodes[0].costPerUnit == variation.costPerUnit
      );
    },
    async fetchIcdCodeOptions(search, loading) {
      if (this.IcdCodesTimeout) {
        clearTimeout(this.IcdCodesTimeout);
        this.IcdCodesTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.icdCodes = [];
      }

      this.IcdCodesTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const icdCodes = await icdCodeServer.query(
            { searchPhrase: search },
            {
              "Hide-Loading": true
            }
          );
          this.icdCodes = icdCodes.map(icdCode => ({
            value: icdCode.id,
            label: `${icdCode.code} - ${icdCode.description}`,
            data: icdCode
          }));
        } else this.icdCodes = [];
        loading(false);
      }, 1000);
    },
    openLinkedDocument(document) {
      if (
        document.document &&
        document.document.status &&
        document.document.status.toLowerCase() == "completed"
      ) {
        this.documentViewModal = {
          show: true,
          id: document.document.id
        };
      } else {
        this.showAddDocumentEditor = true;
        this.editDocument = {
          eocId: this.billing.eocId,
          clientId: this.billing.patientId,
          formId: document.formId,
          submitId: document.document?.id
        };
      }
    },
    async onModifyAddDocument() {
      this.showAddDocumentEditor = false;
      this.documentViewModal.show = false;
      this.documentViewModal.id = null;

      this.fetchBillingData();
    }
  },
  computed: {
    ...mapState("layout", {
      isPracticeSuiteActive: state => state.isPracticeSuiteActive
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    srvOptions() {
      return this.servicesOptions.filter(item => {
        return !this.selectedServices.some(
          selectedService => selectedService.value === item.value
        );
      });
    }
  },
  watch: {
    serviceCostVariations: {
      handler: function(newVariations, oldVariations) {
        oldVariations.forEach(oldVariation => {
          const newVariation = newVariations.find(
            variation => variation.serviceId === oldVariation.serviceId
          );
          if (!newVariation) {
            this.selectedServices.forEach(service => {
              if (service.value === oldVariation.serviceId) {
                let defaultService = this.services.find(
                  item => item.value === oldVariation.serviceId
                );
                service.billingCodes[0].costPerUnit =
                  defaultService.costPerUnit;
                service.billingCodes[0].mod1 =
                  defaultService.billingCodes[0].mod1;
                service.billingCodes[0].mod2 =
                  defaultService.billingCodes[0].mod2;
                service.billingCodes[0].mod3 =
                  defaultService.billingCodes[0].mod3;
                service.billingCodes[0].mod4 =
                  defaultService.billingCodes[0].mod4;
              }
            });
          }
        });
      },
      deep: true
    },
    icdCodeIds: async function(value) {
      if (value && value.length > 0) {
        const diagnosisCodes = await diagnosisCodeServer.query(
          true,
          this.billing.patientId
        );
        this.icdCodeIds.forEach(item => {
          const diagnosisCodeElement = diagnosisCodes.find(
            x => x.icdCode.id == item.value
          );
          if (
            diagnosisCodeElement != null &&
            item.label.substring(0, 5) != "<span"
          ) {
            const newLabel = diagnosisCodeElement.category
              ? `<span class="badge text-white mr-1 p-1 badge-primary">${diagnosisCodeElement.category[0]}</span> ${item.label}`
              : "";
            item.label = newLabel;
            item.category = diagnosisCodeElement.category;
            this.icdCodeIds.sort((a, b) => {
              return a.category < b.category
                ? -1
                : a.category > b.category
                ? 1
                : 0;
            });
          }
        });
      }
    },
    selectedServices: {
      handler: async function(newValue, oldValue) {
        if (
          this.formPatientInsurances &&
          this.selectedServices &&
          this.selectedServices.length > 0 &&
          this.billing &&
          newValue != oldValue
        ) {
          await this.getServicesCostVariations();
        }
        if (newValue.length > oldValue.length) {
          const scheduleEvent = await scheduleEventServer.get(
            this.billing.scheduleEventId
          );
          if (
            scheduleEvent.startDateTime != null &&
            scheduleEvent.endDateTime != null
          ) {
            let serviceIds = [];
            for (let service of newValue) {
              serviceIds.push(service.value);
            }
            let serviceIdsOld = [];
            for (let service of oldValue) {
              serviceIdsOld.push(service.value);
            }
            let status =
              scheduleEvent.type == "groupSession"
                ? "Show"
                : scheduleEvent.status.name;
            let servicePerUnit = await scheduleEventServer.calculateNumberOfUnitByApptDuration(
              scheduleEvent.startDateTime,
              scheduleEvent.endDateTime,
              status,
              serviceIds
            );
            if (Object.keys(servicePerUnit).length > 0) {
              newValue.forEach(service => {
                service.billingCodes.forEach(bc => {
                  let numberOfUnits = servicePerUnit[service.value];
                  bc.numberOfUnits =
                    this.billing.scheduleEventId != null &&
                    serviceIdsOld.length == 0 &&
                    !this.servicesEmpty
                      ? bc.numberOfUnits
                      : this.billing.scheduleEventId != null &&
                        !this.servicesEmpty &&
                        serviceIdsOld.includes(service.value)
                      ? bc.numberOfUnits
                      : numberOfUnits;
                });
              });
            }
          }
        }
        if (oldValue.length > newValue.length && newValue.length == 0) {
          this.servicesEmpty = true;
        } else {
          this.servicesEmpty = false;
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.section-body {
  min-height: 70px;
}

.modal-col {
  margin: 20px;
}

.badge-fixed-size {
  min-width: 101px;
  margin: 0 10px;
}

.add-button {
  max-height: 35px;
  margin: 30px 10px;
}
</style>
